import React, { useState } from "react";
import { Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
    useFloating,
    useClick,
    useDismiss,
    useInteractions,
    FloatingPortal,
    offset,
    flip,
    shift,
} from "@floating-ui/react";
import PathwaySegmentedControl from "../PathwaySegmentedControl";

function NavTabDropdown({ label, isActive, isOpen: controlledOpen, onOpenChange, children }) {
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setIsOpen = onOpenChange || setInternalOpen;

    const { refs, floatingStyles, context } = useFloating({
        placement: "bottom-start",
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    return (
        <>
            <button
                ref={refs.setReference}
                type="button"
                className={classnames("nav-link", { active: isActive, "pw-tab-open": isOpen })}
                aria-expanded={isOpen}
                {...getReferenceProps()}
            >
                <strong>{label}</strong>
            </button>
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className="dropdown-menu show"
                        style={floatingStyles}
                        {...getFloatingProps()}
                    >
                        {children}
                    </div>
                </FloatingPortal>
            )}
        </>
    );
}

const TabNavigation = ({
    nameIdentifier,
    tabIdentifier,
    defaultDisplayedDropdownMenu,
    toggleActiveContentCard,
    changeDisplayDropdownMenu,
    tabList,
    displayedDropdownMenu,
    activeTab,
    navigationSplitIndex,
    variant,
    rightContent,
}) => {
    const splitIndex = navigationSplitIndex ? navigationSplitIndex : 2;

    if (variant === "segmented") {
        const segments = (tabList || []).map((item) => ({
            value: item[tabIdentifier],
            label: item[nameIdentifier],
        }));

        return (
            <SegmentedBar>
                {tabList && (
                    <PathwaySegmentedControl
                        segments={segments}
                        value={activeTab}
                        onChange={(value) => {
                            if (value !== activeTab) {
                                toggleActiveContentCard(value);
                                changeDisplayDropdownMenu(defaultDisplayedDropdownMenu);
                            }
                        }}
                        ariaLabel="Buchungsregeln-Kategorie wählen"
                    />
                )}
                {rightContent && <RightSlot>{rightContent}</RightSlot>}
            </SegmentedBar>
        );
    }

    return (
        <>
            {tabList && (
                <Nav tabs>
                    {tabList.slice(0, splitIndex).map((item, index) => (
                        <NavItem
                            key={index}
                            style={{
                                cursor: activeTab === tabList[index][tabIdentifier] ? undefined : "pointer",
                            }}
                        >
                            <NavLink
                                data-cy={tabList[index][nameIdentifier].replace(/\s+/g, "")}
                                className={classnames({ active: activeTab === tabList[index][tabIdentifier] })}
                                onClick={() => {
                                    toggleActiveContentCard(tabList[index][tabIdentifier]);
                                    changeDisplayDropdownMenu(defaultDisplayedDropdownMenu);
                                }}
                                disabled={activeTab === tabList[index][tabIdentifier]}
                            >
                                <strong>{tabList[index][nameIdentifier]}</strong>&nbsp;
                            </NavLink>
                        </NavItem>
                    ))}

                    {tabList.length > splitIndex && (
                        <NavItem>
                            <NavTabDropdown
                                label={displayedDropdownMenu}
                                isActive={tabList.slice(splitIndex).some((t) => t[tabIdentifier] === activeTab)}
                            >
                                {tabList.slice(splitIndex).map((item, index) => (
                                    <button
                                        key={index}
                                        className={classnames("dropdown-item", {
                                            active: activeTab === item[tabIdentifier],
                                        })}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                            toggleActiveContentCard(tabList.slice(splitIndex)[index][tabIdentifier]);
                                            if (activeTab === item[tabIdentifier]) {
                                                changeDisplayDropdownMenu(defaultDisplayedDropdownMenu);
                                            } else {
                                                changeDisplayDropdownMenu(item[nameIdentifier]);
                                            }
                                        }}
                                    >
                                        <strong>{item[nameIdentifier]}</strong>
                                    </button>
                                ))}
                            </NavTabDropdown>
                        </NavItem>
                    )}
                </Nav>
            )}
        </>
    );
};

export default TabNavigation;

TabNavigation.propTypes = {
    defaultDisplayedDropdownMenu: PropTypes.string.isRequired,
    tabList: PropTypes.array.isRequired,
    nameIdentifier: PropTypes.string.isRequired,
    tabIdentifier: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(["tabs", "segmented"]),
    rightContent: PropTypes.node,
};

const SegmentedBar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--gap-m);
    padding: var(--gap-xl) var(--gap-xxl);
    border-bottom: 1px solid var(--border-hairline);
`;

const RightSlot = styled.div`
    margin-left: auto;
`;
